import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Plate, StatusBadge } from "@/components/StatusBadge";
import { formatRupiah, formatDate, daysSince } from "@/lib/constants";

const ACTIVE = ["BILLING", "INVOICE", "RECEIVABLE"];

export default function Billing() {
  const [claims, setClaims] = useState([]);
  const navigate = useNavigate();
  useEffect(() => { api.get("/claims").then((r) => setClaims(r.data.filter((c) => ACTIVE.includes(c.status)))); }, []);

  return (
    <div>
      <h1 className="font-heading text-2xl font-bold text-[#111827] sm:text-3xl">Billing</h1>
      <p className="mt-1 text-sm text-gray-500">{claims.length} active financial claim{claims.length !== 1 ? "s" : ""} (Billing / Invoice / Receivable). Paid & closed remain accessible via Claim Detail and History.</p>

      {/* Desktop table */}
      <div className="mt-6 hidden overflow-hidden rounded-lg border border-gray-100 bg-white shadow-sm lg:block">
        <table className="w-full text-left text-sm">
          <thead><tr className="border-b border-gray-100 bg-gray-50 text-xs font-semibold uppercase tracking-wider text-gray-500">
            <th className="px-4 py-3">Plate</th><th className="px-4 py-3">WO</th><th className="px-4 py-3">Customer</th><th className="px-4 py-3">Insurance</th><th className="px-4 py-3">Invoice No.</th>
            <th className="px-4 py-3">Invoice Amount</th><th className="px-4 py-3">Due</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Aging</th>
          </tr></thead>
          <tbody>
            {claims.map((c) => (
              <tr key={c.id} data-testid={`billing-row-${c.id}`} onClick={() => navigate(`/claims/${c.id}`)} className="cursor-pointer border-b border-gray-50 transition-colors hover:bg-gray-50">
                <td className="px-4 py-3"><Plate value={c.license_plate} /></td>
                <td className="px-4 py-3">{c.wo_number ? <span className="inline-flex items-center rounded bg-blue-500/15 px-1.5 py-0.5 font-mono text-[11px] font-bold text-[#60A5FA] ring-1 ring-inset ring-blue-400/30">{c.wo_number}</span> : <span className="text-gray-400">-</span>}</td>
                <td className="px-4 py-3 text-gray-700">{c.customer_name || "-"}</td>
                <td className="px-4 py-3 text-gray-600">{c.insurance_company || "-"}</td>
                <td className="px-4 py-3 text-gray-600">{c.invoice_number || "-"}</td>
                <td className="px-4 py-3 font-medium text-gray-800">{formatRupiah(c.invoice_amount)}</td>
                <td className="px-4 py-3 text-gray-500">{formatDate(c.payment_due_date)}</td>
                <td className="px-4 py-3"><StatusBadge status={c.status} /></td>
                <td className="px-4 py-3 text-gray-500">{daysSince(c.updated_at)}d</td>
              </tr>
            ))}
            {claims.length === 0 && <tr><td colSpan={9} className="px-4 py-12 text-center text-gray-400">No active billing claims.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Mobile vertical cards (no horizontal scroll) */}
      <div className="mt-6 space-y-3 lg:hidden">
        {claims.map((c) => (
          <button key={c.id} data-testid={`billing-card-${c.id}`} onClick={() => navigate(`/claims/${c.id}`)} className="block w-full rounded-lg border border-gray-100 bg-white p-4 text-left shadow-sm">
            <div className="flex items-center justify-between">
              <Plate value={c.license_plate} />
              <StatusBadge status={c.status} />
            </div>
            {c.wo_number && <div className="mt-2"><span className="inline-flex items-center rounded bg-blue-500/15 px-2 py-0.5 font-mono text-xs font-bold text-[#60A5FA] ring-1 ring-inset ring-blue-400/30">{c.wo_number}</span></div>}
            <div className="mt-3 text-sm font-medium text-gray-800">{c.customer_name || "-"}</div>
            <div className="text-sm text-gray-500">{c.insurance_company || "-"}</div>
            <div className="mt-2 flex items-center justify-between text-sm">
              <span className="text-gray-500">{c.invoice_number || "No invoice #"}</span>
              <span className="font-semibold text-gray-800">{formatRupiah(c.invoice_amount)}</span>
            </div>
            <div className="mt-1 flex items-center justify-between text-xs text-gray-500">
              <span>Due: {formatDate(c.payment_due_date)}</span>
              <span>{daysSince(c.updated_at)}d aging</span>
            </div>
          </button>
        ))}
        {claims.length === 0 && <p className="py-8 text-center text-sm text-gray-400">No active billing claims.</p>}
      </div>
    </div>
  );
}
